import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { LineChart, Mail, Eye, MousePointerClick, RefreshCw, Inbox } from 'lucide-react';
import { API } from '../lib/api';
import { toast } from '../hooks/use-toast';
import { fmtDate } from '../lib/format';

const fmtTs = (ts) => {
  if (!ts) return '\u2014';
  const d = new Date(ts);
  if (isNaN(d)) return ts;
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const StatTile = ({ icon: Icon, label, value, accent, sub }) => (
  <div className="border border-brand-border rounded-lg p-4 bg-white">
    <div className="flex items-start justify-between">
      <div>
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
        <div className="text-2xl font-bold text-brand-indigo mt-1">{value}</div>
        {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
      </div>
      <div className={`h-9 w-9 rounded-md flex items-center justify-center ${accent}`}>
        <Icon className="h-4 w-4 text-white" />
      </div>
    </div>
  </div>
);

export const DigestAnalytics = () => {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/alerts/digest/analytics?limit=20`, { credentials: 'include' });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Failed');
      setData(j);
    } catch (e) {
      toast({ title: 'Analytics load failed', description: e.message });
    } finally { setLoading(false); }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const s = data?.summary || { sent: 0, opens: 0, clicks: 0, open_rate: 0, click_rate: 0 };
  const digests = data?.digests || [];

  return (
    <Card className="border-brand-border">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <LineChart className="h-4 w-4 text-brand-indigo" />
            <div>
              <div className="text-sm font-semibold text-brand-indigo">Digest Analytics</div>
              <div className="text-[11px] text-muted-foreground">Open and click-through on Monday coaching digests</div>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={load} disabled={loading} className="h-8 text-brand-indigo">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''} mr-1.5`} /> Refresh
          </Button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <StatTile icon={Mail} label="Digests sent" value={s.sent} accent="bg-brand-indigo" sub="last 20 tracked" />
          <StatTile icon={Eye} label="Total opens" value={s.opens} accent="bg-brand-cyan" sub={`${s.open_rate}% open rate`} />
          <StatTile icon={MousePointerClick} label="Total clicks" value={s.clicks} accent="bg-brand-green" sub={`${s.click_rate}% click rate`} />
          <StatTile icon={Inbox} label="Unopened" value={Math.max(0, s.sent - digests.filter((d) => d.open_count > 0).length)} accent="bg-brand-amber" sub="digests with 0 opens" />
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b border-brand-border">
                <th className="px-3 py-2 font-semibold">Digest</th>
                <th className="px-3 py-2 font-semibold">Recipient</th>
                <th className="px-3 py-2 font-semibold text-right">Alerts</th>
                <th className="px-3 py-2 font-semibold text-right">Opens</th>
                <th className="px-3 py-2 font-semibold text-right">Clicks</th>
                <th className="px-3 py-2 font-semibold">Last open</th>
              </tr>
            </thead>
            <tbody>
              {digests.map((d) => (
                <tr key={d.id} className="border-b border-brand-border/40">
                  <td className="px-3 py-2 text-brand-indigo">
                    <div className="font-medium">{d.week_of || fmtDate(d.sent_at)}</div>
                    <div className="text-[10px] text-muted-foreground">Sent {fmtTs(d.sent_at)}</div>
                  </td>
                  <td className="px-3 py-2 text-muted-foreground text-xs">{d.recipient_email}</td>
                  <td className="px-3 py-2 text-right font-semibold text-brand-indigo">{d.alerts_count}</td>
                  <td className="px-3 py-2 text-right">
                    <Badge variant="outline" className={d.open_count > 0 ? 'text-emerald-700 border-emerald-300 bg-emerald-50' : 'text-muted-foreground border-brand-border'}>{d.open_count}</Badge>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Badge variant="outline" className={d.click_count > 0 ? 'text-brand-indigo border-brand-indigo/30 bg-brand-bg' : 'text-muted-foreground border-brand-border'}>{d.click_count}</Badge>
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{fmtTs(d.last_opened_at)}</td>
                </tr>
              ))}
              {digests.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-sm text-muted-foreground">No digests sent yet. Use the “Weekly Digest” button to send one.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-3 text-[10px] text-muted-foreground leading-relaxed">
          Opens are captured via a 1×1 tracking pixel in the email HTML • clicks are logged via a redirect endpoint. Both work today; they will start firing the moment you plug in a real email provider (SendGrid / SMTP).
        </div>
      </CardContent>
    </Card>
  );
};

export default DigestAnalytics;
